import React from 'react'
import {trpcSkillLessons} from '../utils/trpc-skill-lessons'
import {useForm} from 'react-hook-form'
import {z} from 'zod'
import {zodResolver} from '@hookform/resolvers/zod'
import {motion} from 'framer-motion'
import {
  Button,
  Calendar,
  Checkbox,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@skillrecordings/ui'
import {SanityProduct} from '@skillrecordings/commerce-server/dist/@types'
import {cn} from '../utils/cn'
import {format} from 'date-fns'
import {CalendarIcon, Check, ChevronsUpDown, Search} from 'lucide-react'
import Spinner from '../spinner'
import toast from 'react-hot-toast'

const formSchema = z.object({
  quantity: z.string(),
  // coupon
  maxUses: z.string(),
  expires: z.date().optional(),
  restrictedToProductId: z.string().optional(),
  percentOff: z.string(),
  setAsDefault: z.boolean(),
})

const CouponGeneratorForm = ({
  buyUrl = process.env.NEXT_PUBLIC_URL,
  queryParam = 'code',
}: {
  buyUrl?: string
  queryParam?: string
}) => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      quantity: '1',
      maxUses: '-1',
      restrictedToProductId: undefined,
      percentOff: '20',
      expires: undefined,
      setAsDefault: false,
    },
  })
  const [codes, setCodes] = React.useState<string[]>([])
  const [open, setOpen] = React.useState(false)
  const [searchTerm, setSearchTerm] = React.useState('')
  const {data: products} = trpcSkillLessons.products.getAllProducts.useQuery()
  const expiresAtDateTime = form.watch('expires')?.setHours(23, 59, 0, 0)
  const createCouponsMutation = trpcSkillLessons.coupons.create.useMutation()
  const onSubmit = (values: z.infer<typeof formSchema>) => {
    createCouponsMutation.mutate(
      {
        ...values,
        buyUrl,
        queryParam,
      },
      {
        onSuccess: ({codes}) => {
          setCodes(codes)
          if (codes.length > 1) {
            downloadTextFile(codes.join('\n'))
          } else {
            navigator.clipboard.writeText(codes.join('\n'))
            toast.success('Copied to clipboard')
          }
        },
      },
    )
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <fieldset className="grid-cols-5 gap-5 md:grid space-y-5 md:space-y-0">
          <FormField
            name="percentOff"
            render={({field}) => (
              <FormItem>
                <FormLabel htmlFor="percentOff" className="flex h-4">
                  Discount Percentage
                </FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    id="percentOff"
                    {...field}
                    required
                    onChange={field.onChange}
                    placeholder={'20'}
                  />
                </FormControl>
                <FormDescription>Required</FormDescription>
              </FormItem>
            )}
          />
          <FormField
            name="restrictedToProductId"
            render={({field}) => (
              <FormItem className="flex flex-col">
                <FormLabel
                  htmlFor="enableRestrictedToProductId"
                  className="mb-0.5 mt-1.5 flex items-center gap-1.5"
                >
                  <Checkbox
                    id="enableRestrictedToProductId"
                    checked={Boolean(form.watch('restrictedToProductId'))}
                    onCheckedChange={() => {
                      return Boolean(form.watch('restrictedToProductId'))
                        ? form.setValue('restrictedToProductId', undefined)
                        : form.setValue(
                            'restrictedToProductId',
                            process.env.NEXT_PUBLIC_DEFAULT_PRODUCT_ID,
                          )
                    }}
                  />
                  Restricted to Product
                </FormLabel>
                <FormControl>
                  <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        className="w-full justify-between font-normal"
                        disabled={!Boolean(form.watch('restrictedToProductId'))}
                      >
                        {field.value
                          ? products?.find(
                              (product: SanityProduct) =>
                                product.productId === field.value,
                            )?.title ||
                            products?.find(
                              (product: SanityProduct) =>
                                product.productId === field.value,
                            )?.name
                          : Boolean(form.watch('restrictedToProductId'))
                          ? 'Select a product'
                          : 'Global'}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[300px] p-0" align="start">
                      <div className="flex items-center border-b px-3">
                        <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                        <input
                          className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                          placeholder="Search products..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                      <div className="max-h-[300px] overflow-y-auto p-1">
                        {products
                          ?.filter((product: SanityProduct) =>
                            (product.title || product.name || '')
                              .toLowerCase()
                              .includes(searchTerm.toLowerCase()),
                          )
                          .map((product: SanityProduct) => (
                            <div
                              key={product.productId}
                              className={cn(
                                'relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground',
                                field.value === product.productId &&
                                  'bg-accent text-accent-foreground',
                              )}
                              onClick={() => {
                                field.onChange(product.productId)
                                setOpen(false)
                              }}
                            >
                              <Check
                                className={cn(
                                  'mr-2 h-4 w-4',
                                  field.value === product.productId
                                    ? 'opacity-100'
                                    : 'opacity-0',
                                )}
                              />
                              {product.title || product.name}
                            </div>
                          ))}
                        {products?.filter((product: SanityProduct) =>
                          (product.title || product.name || '')
                            .toLowerCase()
                            .includes(searchTerm.toLowerCase()),
                        ).length === 0 && (
                          <div className="py-6 text-center text-sm">
                            No product found.
                          </div>
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            name="expires"
            render={({field}) => (
              <FormItem className="flex flex-col">
                <FormLabel
                  htmlFor="enableExpires"
                  className="mb-0.5 mt-1.5 flex items-center gap-1.5"
                >
                  <Checkbox
                    id="enableExpires"
                    checked={Boolean(form.watch('expires'))}
                    onCheckedChange={() => {
                      return Boolean(form.watch('expires'))
                        ? form.setValue('expires', undefined)
                        : form.setValue('expires', new Date())
                    }}
                  />
                  Expiration date
                </FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={'outline'}
                        className={cn(
                          'w-[240px] pl-3 text-left font-normal',
                          !field.value && 'text-muted-foreground',
                        )}
                      >
                        {field.value ? (
                          format(field.value, 'PPP')
                        ) : (
                          <span>Pick a date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={(date) => {
                        return field.onChange(date)
                      }}
                      disabled={(date) =>
                        date < new Date() || date < new Date('1900-01-01')
                      }
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormDescription>
                  {/* {form.watch('expires')?.toUTCString()} */}
                  {expiresAtDateTime &&
                    new Date(expiresAtDateTime).toISOString()}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            name="maxUses"
            render={({field}) => (
              <FormItem className="flex flex-col gap-0.5">
                <FormLabel
                  htmlFor="enableMaxUses"
                  className="mt-1.5 flex items-center gap-1.5"
                >
                  <Checkbox
                    id="enableMaxUses"
                    checked={form.watch('maxUses') !== '-1'}
                    // onChange={() => form.setValue('maxUses', '1')}
                    onCheckedChange={() => {
                      return form.getValues('maxUses') === '1'
                        ? form.setValue('maxUses', '-1')
                        : form.setValue('maxUses', '1')
                    }}
                  />
                  Limit max uses
                </FormLabel>
                <FormControl>
                  {form.watch('maxUses') === '-1' ? (
                    <Button
                      onClick={() => {
                        form.setValue('maxUses', '1')
                      }}
                      size="sm"
                      variant="ghost"
                      className="h-10 justify-start border border-input text-left text-base text-opacity-60"
                    >
                      Set
                    </Button>
                  ) : (
                    <Input
                      disabled={form.watch('maxUses') === '-1'}
                      type="number"
                      id="maxUses"
                      {...field}
                      required
                      onChange={(e) => {
                        if (e.currentTarget.value === '0') {
                          form.setValue('maxUses', '-1')
                        } else {
                          return field.onChange(e)
                        }
                      }}
                      placeholder="-1"
                    />
                  )}
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            name="setAsDefault"
            render={({field}) => (
              <FormItem className="flex flex-col gap-0.5">
                <FormLabel
                  htmlFor="setAsDefault"
                  className="mt-1.5 flex items-center gap-1.5"
                >
                  <Checkbox
                    id="setAsDefault"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                  Auto apply
                </FormLabel>
                <FormDescription>
                  When enabled, this coupon gets applied automatically (sets
                  default flag)
                </FormDescription>
              </FormItem>
            )}
          />
        </fieldset>
        <div className="flex items-end gap-5 mt-8">
          <div className="flex sm:items-end gap-5 justify-between w-full sm:flex-row flex-col">
            <div className="flex gap-5 items-end">
              <FormField
                name="quantity"
                render={({field}) => (
                  <FormItem>
                    <FormLabel>Quantity</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        id="quantity"
                        {...field}
                        required
                        min={1}
                        max={100}
                        onChange={field.onChange}
                        defaultValue={1}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <Button disabled={form.formState.isSubmitting} type="submit">
                Generate{' '}
                {form.formState.isSubmitting && (
                  <Spinner className="ml-1 w-4 h-5" />
                )}
              </Button>
            </div>
            <motion.div
              key={codes.join('\n')}
              animate={{opacity: form.formState.isSubmitted ? [1, 0.5, 1] : 1}}
              className="w-full flex sm:justify-end items-end gap-2"
            >
              {form.formState.isSubmitted && codes && (
                <>
                  <Button
                    disabled={form.formState.isSubmitting}
                    type="button"
                    onClick={() => downloadTextFile(codes.join('\n'))}
                    className="bg-foreground text-background"
                  >
                    Download
                  </Button>
                  <Button
                    disabled={form.formState.isSubmitting}
                    type="button"
                    onClick={() => {
                      toast.success('Copied to clipboard')
                      return navigator.clipboard.writeText(codes.join('\n'))
                    }}
                    variant="secondary"
                  >
                    Copy to clipboard
                  </Button>
                </>
              )}
            </motion.div>
          </div>
        </div>
      </form>
    </Form>
  )
}

export default CouponGeneratorForm

const downloadTextFile = (textData: string) => {
  const blob = new Blob([textData], {type: 'text/plain'})
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'codes.csv'
  a.click()
  URL.revokeObjectURL(url)
}
